import type { StatefulDiaryT } from "@/types/database/diaries";
import type { EntryValueViewT } from "@/types/database/entries";
import type { ExtendedTrackerViewT } from "@/types/database/trackers";

/**
 * Helper function to round a number to 2 decimal places
 * @param value The number to round
 * @returns The rounded number or null if the input is null
 */
function roundToTwoDecimals(value: number | null): number | null {
	if (value === null) return null;
	return Math.round(value * 100) / 100;
}

// Define the interface for chart data items
export interface ChartDataItem {
	date: string;
	timing: string;
	schedule_index: number;
	schedule_id: string;
	value?: number;
	min_value?: number;
	max_value?: number;
	std_deviation?: number;
	entries_count: number;
	[key: string]: number | string | undefined; // Allow for dynamic keys like "WOMAC Pain Score"
}

/**
 * Extract data from trackers with specific form IDs
 *
 * @param diaries Array of StatefulDiaryT objects to process
 * @returns Object containing processed data for each form type
 */

import { formIds } from "../constants";

export function extractFormData(diaries: StatefulDiaryT[]) {
	// Define form IDs for each form type

	// Initialize result object with the new structure
	const result = {
		womac: [] as Array<{
			schedule_index: number;
			schedule_id: string;
			timing_label: string;
			entry_values: Array<{
				input_id: string;
				input_label: string;
				average: number | null;
				entries_count: number;
				min_value: number | null;
				max_value: number | null;
				std_deviation: number | null;
			}>;
		}>,
		eq5d: [] as Array<{
			schedule_index: number;
			schedule_id: string;
			timing_label: string;
			entry_values: Array<{
				input_id: string;
				input_label: string;
				average: number | null;
				entries_count: number;
				min_value: number | null;
				max_value: number | null;
				std_deviation: number | null;
			}>;
		}>,
		painScore: [] as Array<{
			schedule_index: number;
			schedule_id: string;
			timing_label: string;
			entry_values: Array<{
				input_id: string;
				input_label: string;
				average: number | null;
				entries_count: number;
				min_value: number | null;
				max_value: number | null;
				std_deviation: number | null;
			}>;
		}>,
		oxfordKnee: [] as Array<{
			schedule_index: number;
			schedule_id: string;
			timing_label: string;
			entry_values: Array<{
				input_id: string;
				input_label: string;
				average: number | null;
				entries_count: number;
				min_value: number | null;
				max_value: number | null;
				std_deviation: number | null;
			}>;
		}>,
	};

	// Collect all trackers for each form type across all diaries
	const allTrackersByFormId = {
		womac: [] as ExtendedTrackerViewT[],
		eq5d: [] as ExtendedTrackerViewT[],
		painScore: [] as ExtendedTrackerViewT[],
		oxfordKnee: [] as ExtendedTrackerViewT[],
	};

	// Process each diary
	for (const diary of diaries) {
		if (!diary.trackers_view) {
			console.log(`[Extract] Diary ${diary.diary_id} has no trackers_view`);
			continue;
		}

		// Collect WOMAC trackers
		const womacTrackers = diary.trackers_view.filter(
			(tracker) => tracker.form_id === formIds.womac,
		);
		allTrackersByFormId.womac.push(...womacTrackers);

		// Collect EQ-5D trackers
		const eq5dTrackers = diary.trackers_view.filter(
			(tracker) => tracker.form_id === formIds.eq5d,
		);
		allTrackersByFormId.eq5d.push(...eq5dTrackers);

		// Collect Pain Score trackers
		const painScoreTrackers = diary.trackers_view.filter(
			(tracker) => tracker.form_id === formIds.painScore,
		);
		allTrackersByFormId.painScore.push(...painScoreTrackers);

		// Collect Oxford Knee trackers
		const oxfordKneeTrackers = diary.trackers_view.filter(
			(tracker) => tracker.form_id === formIds.oxfordKnee,
		);
		allTrackersByFormId.oxfordKnee.push(...oxfordKneeTrackers);
	}

	// Process all trackers for each form type
	processTrackersBySchedule(allTrackersByFormId.womac, result.womac);
	processTrackersBySchedule(allTrackersByFormId.eq5d, result.eq5d);
	processTrackersBySchedule(allTrackersByFormId.painScore, result.painScore);
	processTrackersBySchedule(allTrackersByFormId.oxfordKnee, result.oxfordKnee);

	console.log(result);
	return result;
}

/**
 * Process trackers and extract entry values grouped by schedule_index
 *
 * @param trackers Array of trackers to process
 * @param resultArray Array to store the processed results
 */
function processTrackersBySchedule(
	trackers: ExtendedTrackerViewT[],
	resultArray: Array<{
		schedule_index: number;
		schedule_id: string;
		timing_label: string;
		entry_values: Array<{
			input_id: string;
			input_label: string;
			average: number | null;
			entries_count: number;
			min_value: number | null;
			max_value: number | null;
			std_deviation: number | null;
		}>;
	}>,
) {
	console.log(`[Process] Processing ${trackers.length} trackers`);

	// Collect all entry values from all trackers
	const allEntryValues: Array<
		EntryValueViewT & {
			schedule_index: number;
			schedule_id: string;
			timing_label: string;
		}
	> = [];

	for (const tracker of trackers) {
		if (!tracker.entries_view) {
			console.log(
				`[Process] Tracker ${tracker.tracker_id} has no entries_view`,
			);
			continue;
		}

		for (const entry of tracker.entries_view) {
			if (!entry.entry_values) {
				console.log(`[Process] Entry ${entry.entry_id} has no entry_values`);
				continue;
			}

			const timingLabel = entry.timing_label as string;
			const scheduleId = entry.schedule_id || "";

			// Process each entry value
			for (const ev of entry.entry_values) {
				// Skip if no input label
				if (!ev.input_label) {
					// Special case for pain score input
					if (ev.input_id === "5e336505-82c3-422a-be3c-a7b44d9981e0") {
						console.log("[Process] Using default label for pain score input");
						ev.input_label = "Pain Score";
					} else {
						console.log("[Process] Skipping entry value - no input label");
						continue;
					}
				}

				// Convert string numbers to actual numbers
				let numericValue: number | null = null;
				if (typeof ev.value === "number") {
					numericValue = ev.value;
				} else if (typeof ev.value === "string") {
					const parsed = Number(ev.value);
					if (!Number.isNaN(parsed)) {
						numericValue = parsed;
					}
				}

				if (numericValue !== null) {
					allEntryValues.push({
						input_id: ev.input_id,
						input_label: ev.input_label,
						value: numericValue,
						entry_id: ev.entry_id,
						entry_value_id: ev.entry_value_id,
						created_at: ev.created_at,
						form_id: ev.form_id,
						form_name: ev.form_name,
						input_index: ev.input_index,
						is_required: ev.is_required,
						json_value: ev.json_value,
						label: ev.label,
						rules_key: ev.rules_key,
						calculated_entry_time: ev.calculated_entry_time,
						is_calculated: ev.is_calculated,
						entry_values: ev.entry_values,
						input_key: ev.input_key,
						schedule_index: entry.schedule_index || 0,
						schedule_id: scheduleId,
						timing_label: timingLabel,
					});
				}
			}
		}
	}

	// Group entry values by schedule_index and input_id
	const valuesByScheduleAndInputId = new Map<
		number,
		Map<
			string,
			{
				input_label: string;
				values: number[];
				schedule_id: string;
				timing_label: string;
			}
		>
	>();

	// Process each entry value
	for (const entryValue of allEntryValues) {
		const scheduleIndex = entryValue.schedule_index;
		const inputId = entryValue.input_id;
		const value = entryValue.value as number;
		const scheduleId = entryValue.schedule_id;
		const timingLabel = entryValue.timing_label;

		if (!valuesByScheduleAndInputId.has(scheduleIndex)) {
			valuesByScheduleAndInputId.set(scheduleIndex, new Map());
		}

		const inputMap = valuesByScheduleAndInputId.get(scheduleIndex);
		if (!inputMap) {
			console.log(
				`[Process] Error: inputMap is null for scheduleIndex ${scheduleIndex}`,
			);
			continue;
		}

		if (inputMap.has(inputId)) {
			// Add to existing group
			const group = inputMap.get(inputId);
			if (!group) {
				console.log(`[Process] Error: group is null for inputId ${inputId}`);
				continue;
			}
			group.values.push(value);
		} else {
			// Create new group
			inputMap.set(inputId, {
				input_label: entryValue.input_label,
				values: [value],
				schedule_id: scheduleId,
				timing_label: timingLabel,
			});
		}
	}

	// Calculate averages and add to result
	for (const [scheduleIndex, inputMap] of valuesByScheduleAndInputId) {
		const entryValues: Array<{
			input_id: string;
			input_label: string;
			average: number | null;
			entries_count: number;
			min_value: number | null;
			max_value: number | null;
			std_deviation: number | null;
		}> = [];

		// Get schedule_id and timing_label from the first input in the map
		let scheduleId = "";
		let timingLabel = "";
		const firstInput = inputMap.values().next().value;
		if (firstInput) {
			scheduleId = firstInput.schedule_id;
			timingLabel = firstInput.timing_label;
		}

		for (const [inputId, group] of inputMap) {
			const sum = group.values.reduce((acc, val) => acc + val, 0);
			const average =
				group.values.length > 0 ? sum / group.values.length : null;

			// Calculate min, max, and standard deviation
			const minValue =
				group.values.length > 0 ? Math.min(...group.values) : null;
			const maxValue =
				group.values.length > 0 ? Math.max(...group.values) : null;

			// Calculate standard deviation
			let stdDeviation: number | null = null;
			if (group.values.length > 1 && average !== null) {
				const squaredDifferences = group.values.map(
					(val) => (val - average) ** 2,
				);
				const variance =
					squaredDifferences.reduce((acc, val) => acc + val, 0) /
					(group.values.length - 1);
				stdDeviation = Math.sqrt(variance);
			}

			entryValues.push({
				input_id: inputId,
				input_label: group.input_label,
				average: average,
				entries_count: group.values.length,
				min_value: minValue,
				max_value: maxValue,
				std_deviation: stdDeviation,
			});
		}

		resultArray.push({
			schedule_index: scheduleIndex,
			schedule_id: scheduleId,
			timing_label: timingLabel,
			entry_values: entryValues,
		});
	}

	// Sort by schedule_index
	resultArray.sort((a, b) => a.schedule_index - b.schedule_index);
}

/**
 * Transform the extracted form data into chart data format
 *
 * @param formData The extracted form data
 * @returns Object containing chart data for each form type
 */
export function transformFormDataToChartData(
	formData: ReturnType<typeof extractFormData>,
) {
	console.log("Transforming form data to chart data:", formData);

	// Transform pain score data
	const painScoreData = transformPainScoreData(formData.painScore);
	console.log("Transformed pain score data:", painScoreData);

	// Transform WOMAC data
	const womacData = transformWomacData(formData.womac);
	console.log("Transformed WOMAC data:", womacData);

	// Transform EQ-5D data
	const eq5dData = transformEq5dData(formData.eq5d);
	console.log("Transformed EQ-5D data:", eq5dData);

	// Transform Oxford Knee data
	const oxfordKneeData = transformOxfordKneeData(formData.oxfordKnee);
	console.log("Transformed Oxford Knee data:", oxfordKneeData);

	return {
		painScore: painScoreData,
		womac: womacData,
		eq5d: eq5dData,
		oxfordKnee: oxfordKneeData,
	};
}

/**
 * Transform pain score data to chart format
 */
function transformPainScoreData(
	data: Array<{
		schedule_index: number;
		schedule_id: string;
		timing_label: string;
		entry_values: Array<{
			input_id: string;
			input_label: string;
			average: number | null;
			entries_count: number;
			min_value: number | null;
			max_value: number | null;
			std_deviation: number | null;
		}>;
	}>,
) {
	console.log("Input pain score data:", data);

	const result = data.map((item) => {
		// Find the pain score entry value
		const painScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "Pain Score",
		);

		return {
			date: item.timing_label,
			timing: item.timing_label,
			schedule_index: item.schedule_index,
			schedule_id: item.schedule_id,
			value: roundToTwoDecimals(painScoreEntry?.average || 0),
			entries_count: painScoreEntry?.entries_count || 0,
			min_value: roundToTwoDecimals(painScoreEntry?.min_value || 0),
			max_value: roundToTwoDecimals(painScoreEntry?.max_value || 0),
			std_deviation: roundToTwoDecimals(painScoreEntry?.std_deviation || 0),
		} as ChartDataItem;
	});

	console.log("Output pain score data:", result);
	return result;
}

/**
 * Transform WOMAC data to chart format
 */
function transformWomacData(
	data: Array<{
		schedule_index: number;
		schedule_id: string;
		timing_label: string;
		entry_values: Array<{
			input_id: string;
			input_label: string;
			average: number | null;
			entries_count: number;
			min_value: number | null;
			max_value: number | null;
			std_deviation: number | null;
		}>;
	}>,
) {
	console.log("Input WOMAC data:", data);

	const result = data.map((item) => {
		// Find the WOMAC score entries
		const painScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "WOMAC Pain Score",
		);
		const stiffnessScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "WOMAC Stiffness Score",
		);
		const functionScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "WOMAC Function Score",
		);
		const totalScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "WOMAC Total Score",
		);

		return {
			date: item.timing_label,
			timing: item.timing_label,
			schedule_index: item.schedule_index,
			schedule_id: item.schedule_id,
			"WOMAC Pain Score": roundToTwoDecimals(painScoreEntry?.average || 0),
			"WOMAC Stiffness Score": roundToTwoDecimals(
				stiffnessScoreEntry?.average || 0,
			),
			"WOMAC Function Score": roundToTwoDecimals(
				functionScoreEntry?.average || 0,
			),
			"WOMAC Total Score": roundToTwoDecimals(totalScoreEntry?.average || 0),
			entries_count: painScoreEntry?.entries_count || 0,
		} as ChartDataItem;
	});

	console.log("Output WOMAC data:", result);
	return result;
}

/**
 * Transform EQ-5D data to chart format
 */
function transformEq5dData(
	data: Array<{
		schedule_index: number;
		schedule_id: string;
		timing_label: string;
		entry_values: Array<{
			input_id: string;
			input_label: string;
			average: number | null;
			entries_count: number;
			min_value: number | null;
			max_value: number | null;
			std_deviation: number | null;
		}>;
	}>,
) {
	console.log("Input EQ-5D data:", data);

	const result = data.map((item) => {
		// Find the EQ-5D score entry
		const eq5dScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "EQ-5D-5L Total Score",
		);

		return {
			date: item.timing_label,
			timing: item.timing_label,
			schedule_index: item.schedule_index,
			schedule_id: item.schedule_id,
			value: roundToTwoDecimals(eq5dScoreEntry?.average || 0),
			entries_count: eq5dScoreEntry?.entries_count || 0,
			min_value: roundToTwoDecimals(eq5dScoreEntry?.min_value || 0),
			max_value: roundToTwoDecimals(eq5dScoreEntry?.max_value || 0),
			std_deviation: roundToTwoDecimals(eq5dScoreEntry?.std_deviation || 0),
		} as ChartDataItem;
	});

	console.log("Output EQ-5D data:", result);
	return result;
}

/**
 * Transform Oxford Knee data to chart format
 */
function transformOxfordKneeData(
	data: Array<{
		schedule_index: number;
		schedule_id: string;
		timing_label: string;
		entry_values: Array<{
			input_id: string;
			input_label: string;
			average: number | null;
			entries_count: number;
			min_value: number | null;
			max_value: number | null;
			std_deviation: number | null;
		}>;
	}>,
) {
	console.log("Input Oxford Knee data:", data);

	const result = data.map((item) => {
		// Find the Oxford Knee score entries
		const totalScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "Oxford Knee Score Total",
		);
		const painScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "Oxford Knee Pain Score",
		);
		const functionScoreEntry = item.entry_values.find(
			(ev) => ev.input_label === "Oxford Knee Function Score",
		);

		return {
			date: item.timing_label,
			timing: item.timing_label,
			schedule_index: item.schedule_index,
			schedule_id: item.schedule_id,
			"Oxford Knee Score Total": roundToTwoDecimals(
				totalScoreEntry?.average || 0,
			),
			"Oxford Knee Pain Score": roundToTwoDecimals(
				painScoreEntry?.average || 0,
			),
			"Oxford Knee Function Score": roundToTwoDecimals(
				functionScoreEntry?.average || 0,
			),
			entries_count: painScoreEntry?.entries_count || 0,
		} as ChartDataItem;
	});

	console.log("Output Oxford Knee data:", result);
	return result;
}
